import { TextField, type TextFieldProps } from '@mui/material';
import { useId } from 'react';

import {
  FormField,
  type FormFieldProps,
  type RequireLabelOrAria,
  getLabelOrAriaProps,
} from 'common';

import { type ConfigFieldProps } from './types';
import { useSelectedNodeField } from './useSelectedNodeField';
import { useReactFlowViewContext } from '../../context/context';

interface BlockInnerTextFieldProps extends Partial<TextFieldProps<'outlined'>>, ConfigFieldProps {
  fieldId: string;
}

export function BlockInnerTextField(props: BlockInnerTextFieldProps) {
  const {
    fieldId,
    onChange: onValueChange,
    fieldName,
    'aria-label': ariaLabel,
    ...textFieldProps
  } = props;
  const { field, isFieldErrorVisible } = useSelectedNodeField<string | number>(fieldName, {
    type: textFieldProps.type ?? 'text',
  });
  const { isEditable } = useReactFlowViewContext();

  return (
    <TextField
      {...textFieldProps}
      id={fieldId}
      fullWidth
      slotProps={{
        ...textFieldProps.slotProps,
        htmlInput: {
          'aria-label': ariaLabel,
        },
      }}
      {...field}
      onChange={async (event) => {
        field.onChange(event);
        const inputElement = event.target;
        const { selectionStart, selectionEnd, type } = inputElement;
        // Only set selection range for input types that support it
        if (
          type !== 'number' &&
          type !== 'range' &&
          type !== 'date' &&
          type !== 'time' &&
          type !== 'datetime-local' &&
          type !== 'month' &&
          type !== 'week'
        ) {
          await Promise.resolve().then(() => {
            inputElement.setSelectionRange(selectionStart, selectionEnd);
          });
        }
        onValueChange?.(event);
      }}
      value={field.value ?? ''}
      disabled={!isEditable || textFieldProps.disabled}
      error={isFieldErrorVisible}
    />
  );
}

export type BlockTextFieldProps = Omit<BlockInnerTextFieldProps, 'fieldId'> & {
  formFieldProps?: Partial<Omit<FormFieldProps, 'aria-label' | 'label'>>;
} & RequireLabelOrAria;

export function BlockTextField(props: BlockTextFieldProps) {
  const { fieldName, label, helperText, formFieldProps, ...textFieldProps } = props;
  const { visibleError } = useSelectedNodeField<string | number>(fieldName, {
    type: textFieldProps.type ?? 'text',
  });
  const fieldId = useId();

  return (
    <FormField
      id={fieldId}
      {...getLabelOrAriaProps({ label, 'aria-label': props['aria-label'] })}
      helperText={helperText}
      error={visibleError}
      {...formFieldProps}
    >
      <BlockInnerTextField fieldName={fieldName} {...textFieldProps} fieldId={fieldId} />
    </FormField>
  );
}
